import React, { useEffect, useState } from 'react';
import { X, AlertCircle, Mail } from 'lucide-react';
import { emailService } from 'api/services/email.service';

interface Props {
  campaignId: string;
  recipientId: string;
  email: string;
  onClose: () => void;
}

/**
 * What one recipient actually received.
 *
 * <p>This shows the STORED copy, never a re-render. Re-rendering would answer
 * "what would we send now", which is a different question and wrong the moment the
 * template is edited — and the reason somebody opens this is usually that a
 * recipient is disputing what they got.
 *
 * <p>The body is rendered in a sandboxed iframe rather than with
 * dangerouslySetInnerHTML: this is stored HTML that went out to a real person and
 * may contain anything, and injecting it into the portal's own document would give
 * it the operator's session. `sandbox` with no allow-* tokens blocks scripts,
 * forms, and navigation, so it renders and does nothing else.
 */
export const SentCopyModal: React.FC<Props> = ({ campaignId, recipientId, email, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await emailService.sentCopy(campaignId, recipientId);
      if (!alive) return;
      if (r.success) setData(r.data); else setError(r.message || 'Could not load the sent copy');
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [campaignId, recipientId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100 font-medium">
              <Mail className="w-4 h-4 shrink-0" />
              <span className="truncate">{email}</span>
            </div>
            {data?.sentAt && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Sent {new Date(data.sentAt).toLocaleString()}
                {data.providerMessageId ? ` · ${data.providerMessageId}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0 ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && <p className="px-5 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</p>}

          {error && (
            <div className="px-5 py-6 flex items-start gap-2 text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><p className="text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && data && !data.stored && (
            <div className="px-5 py-6 flex items-start gap-2 text-gray-600 dark:text-gray-300">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-orange-500" />
              <div className="text-sm">
                <p className="font-medium text-gray-800 dark:text-gray-100">No copy was recorded</p>
                {/* Said plainly. "Nothing to show" would read as "nothing was sent", which
                    is a different and much more alarming thing. */}
                <p className="mt-1">{data.note || 'The body exceeded the size cap, or the send failed before the message was rendered.'}</p>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Status {data.status}{data.errorCode ? ` · ${data.errorCode}` : ''}
                </p>
              </div>
            </div>
          )}

          {!loading && !error && data?.stored && (
            <div>
              <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
                <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">Subject</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{data.subject}</p>
              </div>
              <iframe
                title="Sent copy"
                sandbox=""
                srcDoc={data.html || ''}
                className="w-full h-[55vh] bg-white"
              />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
          This is the copy stored at send time, not a fresh render — editing the template does not change it.
        </div>
      </div>
    </div>
  );
};
